import { BCColorPicker, OnColorChangeComplete, InputSizes, findMessage } from '@bimser/components';
import { FormItemTypes, IDictionary } from '@bimser/core';
import { JSX } from 'react';
import { IUserEvent } from '../../../../entities/services/IUserEvent';
import InputControl from '../../base/InputControl';
import { ICloseEventArgs, IOpenEventArgs } from '../../entities/EventArgs';
import { IColorPickerProperties } from './entities/IColorPickerProperties';
import { ColorFormat, TriggerType } from '../../../../entities/common/Types';

export default class ColorPicker extends InputControl<string> {
	disableAlpha?: boolean;
	sizeType: InputSizes;
	presetColors?: string[];
	showText: boolean;
	allowClear: boolean;
	trigger: TriggerType;
	format: ColorFormat;

	constructor() {
		super();
		this.onOpenChange = this.onOpenChange.bind(this);
		this.triggerValueChanged = this.triggerValueChanged.bind(this);
		this.onFormatChange = this.onFormatChange.bind(this);

		this.controlType = FormItemTypes.EControlTypes.ColorPicker;
		this.disableAlpha = true;
		this.value = 'rgba(255, 255, 255, 1)';
		this.presetColors = [];
		this.showText = true;
		this.allowClear = false;
		this.trigger = 'click';
		this.format = 'rgb';

		this.subscribe('trigger:OnValueChanged', (value: string) => {
			this.__proxyChanging.value = value;
		});
	}

	__loadProperties(data: IColorPickerProperties) {
		super.__loadProperties(data);
		this.__proxyChanged.disableAlpha = data.disableAlpha;
		this.__proxyChanged.presetColors = data.presetColors;
		this.__proxyChanged.sizeType = data.sizeType;
		this.__proxyChanged.showText = data.showText;
		this.__proxyChanged.allowClear = data.allowClear;
		this.__proxyChanged.trigger = data.trigger;
		this.__proxyChanged.format = data.format;
	}

	private triggerValueChanged: OnColorChangeComplete = color => {
		this.__proxyChanging.value = color.toRgbString();
	};

	private onOpenChange(open: boolean) {
		const argument = {
			value: this.value
		};

		if (open) {
			this.emit<IOpenEventArgs>('event:OnOpen', argument);
			this.emit<IUserEvent<IOpenEventArgs>>('event:server:OnOpen', {
				argument,
				controlId: this.name,
				name: 'Open',
				sender: 'web'
			});
		} else {
			this.emit<ICloseEventArgs>('event:OnClose', argument);
			this.emit<IUserEvent<ICloseEventArgs>>('event:server:OnClose', {
				argument,
				controlId: this.name,
				name: 'Close',
				sender: 'web'
			});
		}
	}

	private onFormatChange(format: ColorFormat) {
		this.__proxyChanging.format = format;
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			disableAlpha: this.disableAlpha,
			presetColors: this.presetColors,
			sizeType: this.sizeType,
			allowClear: this.allowClear,
			showText: this.showText,
			trigger: this.trigger,
			format: this.format
		};
	}

	__getSizeType() {
		return this.sizeType ?? this.__form.sizeType;
	}

	__render(children?: JSX.Element) {
		return super.__render(
			<BCColorPicker
				value={this.value}
				presets={
					this.presetColors
						? [
								{
									colors: this.presetColors,
									label: findMessage.get('101451')
								}
						  ]
						: undefined
				}
				disabled={this.__isReadOnly() || this.__isDisabled()}
				disabledAlpha={this.allowClear ? false : this.disableAlpha}
				onOpenChange={this.onOpenChange}
				onChangeComplete={this.triggerValueChanged}
				size={this.__getSizeType()}
				style={this.style}
				allowClear={this.allowClear}
				showText={this.showText}
				trigger={this.trigger}
				format={this.format}
				onFormatChange={this.onFormatChange}
			/>
		);
	}
}
